import React, {useState, useEffect} from 'react';
import {CalendarIcon, EyeIcon, ArrowRightIcon, SearchIcon} from 'lucide-react';
import {Link, useParams} from "react-router-dom";
import useApi from "../../../utils/useApi";

const ViewSentLetters = ({ user = [], handleShowHeader, handleShowSettingsHeader }) => {
    const { uid } = useParams();
    const { getSentLetters } = useApi();
    const [client, setClient] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [templates, setTemplates] = useState([]);
    const [filteredTemplates, setFilteredTemplates] = useState([]);
    const [loading, setLoading] = useState(true);

    // Initialize data
    useEffect(() => {
        handleShowHeader(true);
        handleShowSettingsHeader(false);
        const initialize = async () => {
            try {
                setLoading(true);
                const response = await getSentLetters(uid);
                setTemplates(response.templatesSent);
                setFilteredTemplates(response.templatesSent);
                setClient(response.client);
            }
            catch (error) {
                console.error('Error fetching templates:', error);
                setTemplates([]);
                setFilteredTemplates([]);
            }
            finally {
                setLoading(false);
            }
        };

        initialize();
    }, [uid, getSentLetters]);

    // Handle search filtering
    useEffect(() => {
        if (templates.length) {
            const filtered = templates.filter(template =>
                                                  template.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                                  template.recipient_id?.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredTemplates(filtered);
        }
    }, [searchTerm, templates]);

    if (loading) {
        return (
            <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center">
                <div className="text-gray-600 dark:text-gray-300">Loading...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white dark:bg-gray-900 page-container relative z-10">
            <div className="p-4 md:p-6">
                {/* Header Section */}
                <div className="space-y-4 md:space-y-0 md:flex md:justify-between md:items-center mb-6">
                    <div className="flex flex-col">
                        <h3 className="text-xl md:text-2xl font-semibold text-gray-800 dark:text-white">
                            Letters sent to:
                        </h3>
                        <Link
                            to={`/client/${client?.id}/edit`}
                            className="text-blue-500 hover:text-blue-600 dark:text-blue-400"
                        >
                            {client?.profile?.client}
                        </Link>
                    </div>

                    <div className="flex flex-row gap-3">
                        <Link to={`/documents/${client?.id}`}
                              className="inline-flex items-center justify-center gap-2 px-4 py-2
                    min-w-[140px] md:min-w-0
                    text-xs
                    bg-blue-500 dark:bg-blue-600
                    text-white
                    hover:bg-blue-600 dark:hover:bg-blue-700
                    rounded-md transition-colors"
                        >
                            Documents <EyeIcon className="ml-2 h-4 w-4"/>
                        </Link>

                        <Link
                            to={`/send-letter/${client?.id}`}
                            className="inline-flex justify-center items-center px-4 py-2 border border-red-500 text-red-500 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 inline-flex items-center justify-center gap-2 px-4 py-2 min-w-[140px] md:min-w-0 text-xs rounded-md transition-colors"
                        >
                            Send Letter <ArrowRightIcon className="ml-2 h-4 w-4"/>
                        </Link>
                    </div>
                </div>

                {/* Search Bar */}
                <div className="relative mb-6">
                    <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400"/>
                    <input
                        type="text"
                        placeholder="Search templates..."
                        className="w-full pl-10 pr-4 py-2 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                {/* Templates Grid */}
                <div className="space-y-4">
                    {filteredTemplates?.length > 0 ? (
                        filteredTemplates.map((template) => (
                            <div
                                key={template.id}
                                className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 space-y-3"
                            >
                                <div className="flex justify-between items-start">
                                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                        #{template.id}
                                    </span>
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                                        <CalendarIcon className="mr-1 h-3 w-3"/>
                                        {template.created_on}
                                    </span>
                                </div>

                                <div className="space-y-2">
                                    <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                                        {template.title}
                                    </h4>

                                    <div className="flex flex-col space-y-1">
                                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                            Recipient:
                                        </span>
                                        <span className="text-gray-900 dark:text-white break-all">
                                            {template.recipient_id}
                                        </span>
                                    </div>
                                </div>

                                <div className="pt-2">
                                    <Link
                                        to={`/letter/template/${template.template_id}/${template.id}`}
                                        className="w-full inline-flex justify-center items-center px-4 py-2 border border-blue-500 text-blue-500 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                                    >
                                        View Message <EyeIcon className="ml-2 h-4 w-4"/>
                                    </Link>
                                </div>
                            </div>
                        ))
                    ) : (
                         <div className="text-center py-8 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                             No messages sent yet!
                         </div>
                     )}
                </div>
            </div>
        </div>
    );
};

export default ViewSentLetters;
